import { useEffect, useRef } from 'react';
import { protocolPdfsApi } from '../services/api';
import { protocolPdfsSummary } from '../utils/protocolPdfsSummary';
import { saveBlob } from '../utils/download';
import { useProtocolPdfsPolling, UseProtocolPdfsPolling } from './useProtocolPdfsPolling';
import { useSummaryHeatSources } from './useSummaryHeatSources';

export type ProtocolPackMessage = { kind: 'error' | 'info'; text: string };

export interface UseProtocolPdfsPack {
  /** Stan joba (status/result/jobId) — do etykiet przycisków i blokad. */
  job: UseProtocolPdfsPolling;
  /**
   * Start paczki dla wskazanych budynków. `year` jak w selekcie rocznika: '' = wszystkie lata.
   * `complete` (#416) = komplet: protokoły CEEB + podsumowanie kontroli + karty lokali z podpisami.
   */
  start: (buildingIds: string[], year: string, complete?: boolean) => Promise<void>;
}

/**
 * Pobieranie paczki protokołów CEEB / kompletu (E5, #416) — WSPÓLNE dla listy budynków
 * („Pobierz ▾") i widoku budynku (B10, RAPORT Etap 2 #16). Jeden job backendu
 * (POST /buildings/protocol-pdfs), polling przez useProtocolPdfsPolling, po `done` zapis ZIP-a
 * i jedno zdanie podsumowania (protocolPdfsSummary). Opcja „Dołącz informacje o źródłach ciepła"
 * (H7) — wspólny stan panelu, dotyczy tylko kompletu. Komunikaty idą do `onMessage`, bo każdy
 * widok pokazuje je we własnym banerze.
 */
export function useProtocolPdfsPack(onMessage: (msg: ProtocolPackMessage) => void): UseProtocolPdfsPack {
  const job = useProtocolPdfsPolling();
  const [summaryHeatSources] = useSummaryHeatSources();
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const downloadedJobRef = useRef<string | null>(null);

  useEffect(() => {
    if (job.status === 'done' && job.jobId && downloadedJobRef.current !== job.jobId) {
      downloadedJobRef.current = job.jobId;
      protocolPdfsApi.download(job.jobId)
        .then(({ blob, filename }) => {
          saveBlob(blob, filename || 'protokoly_ceeb.zip');
          // BEZWARUNKOWO — do 2026-08-05 komunikat zależał od `skipped`/`noUuidCount`, więc
          // paczka „3 z 8" schodziła bez słowa wyjaśnienia (patrz protocolPdfsSummary).
          const summary = protocolPdfsSummary(job.result);
          if (summary) onMessageRef.current({ kind: 'info', text: summary });
        })
        .catch((e) => onMessageRef.current({
          kind: 'error', text: e instanceof Error ? e.message : 'Pobranie paczki nie powiodło się',
        }));
    }
    if (job.status === 'error') {
      onMessageRef.current({ kind: 'error', text: job.result?.error || 'Pobieranie protokołów z CEEB nie powiodło się' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.status, job.jobId]);

  const start = async (buildingIds: string[], year: string, complete = false) => {
    if (!buildingIds.length || job.status === 'running') return;
    const protocolYear = year ? Number(year) : undefined;
    if (complete && summaryHeatSources) {
      await job.start(buildingIds, protocolYear, ['summaries', 'cards'], { heatSources: true });
    } else {
      await job.start(buildingIds, protocolYear, complete ? ['summaries', 'cards'] : undefined);
    }
  };

  return { job, start };
}
